import {
  abwesendeTage,
  istVerbindlich,
  lageJePerson,
  tagesschluessel,
  tagKompakt,
  type AbwesenheitsLage,
} from '@netcon-work/shared'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { abwesenheitenQuery, type AbwesenheitsZeile } from '../lib/queries'
import { useMerker } from '../lib/merker'

/**
 * Abwesenheiten in der Oberfläche (Zusatzpaket Z-01).
 *
 * ## Ein Ladevorgang, alle Ansichten
 *
 * `useAbwesenheiten` liest **eine** Abfrage und rechnet daraus lokal, was die
 * jeweilige Ansicht braucht – nach Leitplanke 2, genau wie beim Aufgabenbestand.
 * Die Auswertung selbst (`lageJePerson`, `abwesendeTage`) liegt im geteilten
 * Paket, damit der Browser dieselbe Regel anwendet wie der Server, der den
 * Zuweisungs-Vorschlag sortiert.
 *
 * ## Was hier bewusst nicht steht
 *
 * **Keine Art.** Nirgends „Urlaub" oder „krank" – die Schnittstelle liefert es
 * nicht, und für die Arbeitssteuerung zählt „ist da / ist nicht da". Es steht
 * überall „abwesend", und das ist die vollständige Auskunft, die Work hat.
 */

export interface AbwesenheitsSicht {
  /** Alle Abwesenheiten im geladenen Fenster – auch die ohne Zuordnung. */
  liste: AbwesenheitsZeile[]
  /** Heute, als Tagesschlüssel. */
  heute: string
  /** Was für welche Person gerade und als Nächstes gilt. */
  lage: Map<string, AbwesenheitsLage>
  /** Tage im Raster, an denen die Person weg war – für die Zeitleiste. */
  abwesendAn: (personId: string, tage: readonly string[]) => Set<string>
  /**
   * Wer heute nicht da ist – **nur zugeordnete Personen.**
   *
   * Das war zuerst anders, und die Korrektur ist wissenswert: ursprünglich standen
   * hier auch Abwesenheiten ohne Zuordnung, damit eine fehlende Zuordnung sichtbar
   * wird statt Daten zu verschlucken. Bei einem Team von zwölf Personen ist das
   * richtig.
   *
   * Das Intranet führt aber die **ganze epcan** – rund 90 Konten. Die
   * Anwendungsentwicklung ist ein kleiner Teil davon, und der Rest braucht nie eine
   * Zuordnung. „Heute nicht da" hätte damit täglich Kolleginnen und Kollegen aus
   * Buchhaltung und Vertrieb aufgezählt: kein sichtbar gemachter Mangel, sondern
   * Rauschen, das die drei Namen verdeckt, auf die es ankommt.
   *
   * Sichtbar bleibt die Lücke trotzdem – als Zähler in der Zuordnungs-Karte unter
   * *KI & Einstellungen → Benutzer*. Dort gehört sie hin: sie ist eine
   * Verwaltungsaufgabe, keine Tagesinformation.
   */
  heuteAbwesend: { personId: string; name: string; bis: string; halberTag: boolean }[]
  /** Wie viele Abwesenheiten noch keiner Person zugeordnet sind. */
  ohneZuordnung: number
}

export function useAbwesenheiten(): AbwesenheitsSicht {
  const { data } = useQuery(abwesenheitenQuery)
  const liste = data?.abwesenheiten ?? []

  return useMemo(() => {
    const heute = tagesschluessel(new Date())
    const lage = lageJePerson(liste, heute)

    /**
     * Nach Personen vorsortieren, damit `abwesendAn` nicht bei jedem Aufruf die
     * ganze Liste durchgeht. Die Zeitleiste ruft es für jede Person einmal auf –
     * bei 12 Personen × 30 Tagen wäre das sonst spürbar.
     */
    const jePerson = new Map<string, AbwesenheitsZeile[]>()
    for (const a of liste) {
      if (!a.personId) continue
      const bisher = jePerson.get(a.personId)
      if (bisher) bisher.push(a)
      else jePerson.set(a.personId, [a])
    }

    const heuteAbwesend = liste
      // Nur zugeordnete: das Intranet führt die ganze epcan, nicht nur das
      // Entwicklungsteam. Begründung oben am Feld.
      .filter((a) => a.personId !== null && a.von <= heute && heute <= a.bis)
      .map((a) => ({
        personId: a.personId as string,
        name: a.name,
        bis: a.bis,
        halberTag: (a.halberTagStart && heute === a.von) || (a.halberTagEnde && heute === a.bis),
      }))
      // Alphabetisch – die Reihenfolge soll nicht bei jedem Neuladen springen.
      .sort((a, b) => a.name.localeCompare(b.name, 'de'))

    return {
      liste,
      heute,
      lage,
      abwesendAn: (personId, tage) => abwesendeTage(jePerson.get(personId) ?? [], tage),
      heuteAbwesend,
      ohneZuordnung: liste.filter((a) => a.personId === null).length,
    }
  }, [liste])
}

export interface AbwesenheitsAngabe {
  abwesendBis: string | null
  halberTag: boolean
  naechsteVon: string | null
  naechsteBis: string | null
}

/**
 * Kurzhinweis neben einem Namen.
 *
 * Drei Stufen, absichtlich verschieden gewichtet:
 *
 * | Lage | Darstellung |
 * |---|---|
 * | heute weg | farbig hervorgehoben – **das** muss auffallen |
 * | halber Tag | farbig, aber als „halber Tag": die Person ist erreichbar |
 * | kommt noch | grau, klein – eine Auskunft, keine Warnung |
 *
 * Nichts davon hängt allein an der Farbe: jede Stufe trägt ihren eigenen Text.
 */
export function AbwesenheitsHinweis({
  angabe,
  className = '',
}: {
  angabe: AbwesenheitsAngabe | undefined
  className?: string
}) {
  if (!angabe) return null

  if (angabe.abwesendBis) {
    return (
      <span
        className={`shrink-0 rounded-md bg-[#fdf3dc] px-[6px] py-[1.5px] text-[10px] font-semibold text-[#8a6100] ${className}`}
      >
        {angabe.halberTag ? 'halber Tag' : `abwesend bis ${tagKompakt(angabe.abwesendBis)}`}
      </span>
    )
  }

  if (angabe.naechsteVon && tageBisHeute(angabe.naechsteVon) <= BALD_TAGE) {
    return (
      <span className={`shrink-0 text-[10px] text-muted ${className}`}>
        weg {tagKompakt(angabe.naechsteVon)}
        {angabe.naechsteBis && `–${tagKompakt(angabe.naechsteBis)}`}
      </span>
    )
  }

  return null
}

/**
 * **Wie weit voraus eine Abwesenheit noch eine Auskunft ist** (Nils, 20.08.2026).
 *
 * Er sah in der Geplant-Kachel vier Namen mit „abwesend" und fragte: *„Bezieht sich das auf die
 * Vergangenheit? Da sollte das denke ich dann auf heute bezogen sein. Ggf. wenn in den nächsten
 * 5 Tagen weg, dann (23.8. – 1.09) oder so."*
 *
 * Zwei Fehler steckten darin, und nur einer war meiner:
 *
 * 1. **Die Kachel hatte ein eigenes Abzeichen gebaut**, das jede Abwesenheit mit einem Ende in
 *    der Zukunft als „abwesend" auswies – auch eine, die erst im September beginnt. Behoben,
 *    indem beide Tabellen jetzt diesen Hinweis benutzen; er trennt „heute weg" von „kommt noch"
 *    seit Z-01.
 * 2. **Auch „kommt noch" kannte keine Grenze.** Ein Urlaub in elf Wochen stand grau neben dem
 *    Namen und sagte für heute nichts. Fünf Tage ist Nils' Zahl, und sie passt zum Zweck: die
 *    Kachel beantwortet *„was liegt an"*, nicht *„wann ist wer im Urlaub"* – dafür gibt es den
 *    Kalender.
 */
const BALD_TAGE = 5

/** Ganze Tage von heute bis zu einem Tagesschlüssel. Negativ heißt: liegt zurück. */
function tageBisHeute(tagesschluesselWert: string): number {
  const heute = new Date(`${tagesschluessel(new Date())}T12:00:00`)
  const ziel = new Date(`${tagesschluesselWert}T12:00:00`)
  return Math.round((ziel.getTime() - heute.getTime()) / 86_400_000)
}

/** Angabe aus einer Lage bilden – dieselbe Form, die das Backend mitliefert. */
export function angabeAusLage(
  lage: AbwesenheitsLage | undefined,
  heute: string,
): AbwesenheitsAngabe {
  return {
    abwesendBis: lage?.heute?.bis ?? null,
    halberTag: lage?.heute
      ? (lage.heute.halberTagStart && heute === lage.heute.von) ||
        (lage.heute.halberTagEnde && heute === lage.heute.bis)
      : false,
    naechsteVon: lage?.naechste?.von ?? null,
    naechsteBis: lage?.naechste?.bis ?? null,
  }
}

/**
 * „Heute nicht da" – eine Zeile für „Mein Tag".
 *
 * Steht auch dann da, wenn niemand fehlt: eine Karte, die nur bei Abwesenheit
 * erscheint, lässt einen im Zweifel („ist die Liste leer oder fehlt sie?"). Die
 * ausdrückliche Aussage „heute sind alle da" ist die kürzere Antwort.
 *
 * **Aber nur, wenn überhaupt etwas über das Team bekannt ist.** Gibt es keine
 * einzige zugeordnete Abwesenheit, sagt die Zeile nichts – sonst stünde „Heute sind
 * alle da" da, während in Wahrheit nur niemand zugeordnet ist. Das wäre eine
 * Behauptung über das Team, gestützt auf eine fehlende Verwaltungsangabe.
 */
/**
 * **Wie viele Namen die Vorschau trägt** ([E-386](../../../../docs/ENTSCHEIDUNGEN.md#e-386)).
 *
 * Zwei, nicht fünf: Die Zeile soll in **eine** Zeile passen, auch auf einem halben Bildschirm.
 * Ein längster deutscher Name im Bestand („Alexander Keßler-Pfingstmann") ist allein 28
 * Zeichen – bei zweien plus Zahl bleibt die Zeile unter 80 und bricht nirgends um.
 */
const VORSCHAU_NAMEN = 2

export function HeuteNichtDa({ sicht }: { sicht: AbwesenheitsSicht }) {
  const { heuteAbwesend, liste } = sicht
  /**
   * **Zugeklappt, und das bleibt so** ([E-386](../../../../docs/ENTSCHEIDUNGEN.md#e-386),
   * 01.10.2026).
   *
   * Christian Hunhoff: *„Braucht auf Dauer relativ viel Platz vom Screen, dafür dass die Info
   * meistens unwichtig für uns, Entwicklung ist."* Gemessen über 25 Werktage: **13,3 Abwesende
   * im Schnitt, höchstens 31** – die volle Zeile war an einem gewöhnlichen Tag rund 400
   * Zeichen lang und damit drei Zeilen, am 07.09. wären es sieben gewesen. Jeden Tag, ganz oben.
   *
   * Gemerkt wird je **Gerät**, wie jede andere Ansichtsvorliebe: Wer die Namen täglich braucht
   * – die Leitung, die verteilt –, klappt einmal auf und findet sie wieder.
   */
  const [merker, setMerker] = useMerker<'an' | 'aus'>('abwesend-offen', 'aus', ['an', 'aus'])
  const offen = merker === 'an'

  // Nichts geladen, oder nichts Zugeordnetes: nichts sagen. Nicht „alle da"
  // behaupten, solange die Abfrage läuft, der Abgleich nie lief oder keine
  // Zuordnung besteht.
  if (liste.length === 0 || !liste.some((a) => a.personId !== null)) return null

  if (heuteAbwesend.length === 0) {
    return <span className="text-[12px] text-muted">Heute sind alle da.</span>
  }

  const vorschau = heuteAbwesend.slice(0, VORSCHAU_NAMEN)
  const weitere = heuteAbwesend.length - vorschau.length

  return (
    <button
      type="button"
      onClick={() => setMerker(offen ? 'aus' : 'an')}
      aria-expanded={offen}
      title={offen ? 'Zuklappen' : 'Alle Namen zeigen'}
      className="cursor-pointer text-left text-[12px] text-ink2"
    >
      <b className="font-semibold">
        {heuteAbwesend.length} heute nicht da
        {offen ? ':' : ''}
      </b>{' '}
      {/**
       * **Die Vorschau beantwortet die häufigste Frage, ohne die Liste zu sein**: *„ist jemand
       * weg, den ich gerade brauche?"* Zwei Namen reichen dafür erstaunlich oft – und wenn
       * nicht, steht daneben, wie viele es noch sind.
       *
       * Ohne sie wäre die Zeile eine nackte Zahl, und man müsste jeden Tag aufklappen, um sie
       * jeden Tag wieder zuzuklappen.
       */}
      {(offen ? heuteAbwesend : vorschau).map((a, i) => (
        <span key={`${a.personId}-${a.bis}`}>
          {i > 0 && ' · '}
          {a.name}
          {offen && (
            <span className="text-muted">
              {' '}
              {a.halberTag ? '(halber Tag)' : `(bis ${tagKompakt(a.bis)})`}
            </span>
          )}
        </span>
      ))}
      {!offen && weitere > 0 && <span className="text-muted"> +{weitere} weitere</span>}
      <span className="text-muted"> {offen ? '‹' : '›'}</span>
    </button>
  )
}

/** Ob eine Zeile nur beantragt ist – für die Zuordnungs-Ansicht. */
export function istBeantragt(zeile: AbwesenheitsZeile): boolean {
  return !istVerbindlich(zeile)
}
